import { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { useState } from "react";
import { PlaceholderDataFunction, QueryKey, useQuery, UseQueryOptions } from "react-query";
import { api as xolaApi } from "../api/api";
import { queryClient } from "../api/queryClient";
import { generateQueryKey, populateCache, populateResource } from "./helper";
import { Resource, Response } from "@/types/Response";

export interface Options<T> extends Omit<UseQueryOptions<T, AxiosError, T[]>, "placeholderData"> {
    api?: AxiosInstance;
    apiConfig?: AxiosRequestConfig;
    queryKey?: QueryKey;
    // Since this is useCollection, we expect placeHolderData to have or return an array of items
    placeholderData?: T[] | PlaceholderDataFunction<T[]>;
}

export const useCollection = <T extends Resource<K>, K extends string = "id">(
    path: string | string[],
    queryParams?: any,
    options?: Options<T>,
) => {
    const api = options?.api ?? xolaApi;
    const [defaultData] = useState<T[]>([]);
    const [collectionName] = Array.isArray(path) ? path.slice(-1) : [path];

    const params = { limit: 100, ...queryParams };
    const queryKey = options?.queryKey ?? generateQueryKey([collectionName, "collection"], params);

    const query = useQuery<any, AxiosError, T[]>(
        queryKey,
        async () => {
            const url = Array.isArray(path) ? path.join("/") : path;
            const { data } = await api.get<Response<T, Resource>>(`/${url}`, { ...options?.apiConfig, params });
            const collection = Array.isArray(data) ? data : data.data;

            // Pre-populate the cache for individual resources.
            for (const resource of collection) {
                // Avoid creating a query for each timeslot,
                // since there can be up to 9k slots per month in the worst case (every 5 mins).
                if (resource.id && collectionName !== "timeslots") {
                    populateResource(collectionName, resource);
                }

                // If we already created a cache query for a single timeslot,
                // we need to rewrite it to have fresh data, since we can't just invalidate single timeslot.
                if (collectionName === "timeslots") {
                    const existingSlotCache = queryClient.getQueryData(["timeslots", resource?.id]);

                    if (existingSlotCache) {
                        queryClient.setQueryData(["timeslots", resource?.id], resource);
                    }
                }
            }

            populateCache(data, params);

            return collection;
        },
        options,
    );

    return { ...query, data: query.data ?? defaultData };
};
